class B2bSubscribeForm extends HTMLFormElement {
  constructor() {
    super();
    this.addEventListener('submit', this.onSubmitHandler);
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get submitButton() {
    return this.querySelector('[type="submit"]');
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton?.hasAttribute('aria-disabled')) return;

    this.activeElement = event.submitter || this.submitButton;

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    const config = theme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this);
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    this.submitButton?.setAttribute('aria-disabled', 'true');
    this.submitButton?.setAttribute('aria-busy', 'true');

    fetch(`${theme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'b2b-subscribe-form',
            productVariantId: formData.get('id'),
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message
          });
          console.error(parsedState.description || parsedState.message);
          return;
        }

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson.sections = parsedState.sections;

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'b2b-subscribe-form',
          productVariantId: formData.get('id'),
          cart: cartJson
        });

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: parsedState }
        }));

        this.cartDrawer?.show(this.activeElement);
      })
      .catch((error) => console.error(error))
      .finally(() => {
        this.submitButton?.removeAttribute('aria-busy');
        this.submitButton?.removeAttribute('aria-disabled');
      });
  }
}

customElements.define('b2b-subscribe-form', B2bSubscribeForm, { extends: 'form' });

class B2bSubscribeTooltips {
  static ALIGN_CLASSES = [
    'metafield-rich_text_field--align-center',
    'metafield-rich_text_field--align-left',
    'metafield-rich_text_field--align-right'
  ];

  constructor(section) {
    this.section = section;
    this.wrapper = section.querySelector('.b2b-subscribe__wrapper');
    this.tooltips = section.querySelectorAll('.content-item__tooltip');
    this.activeTooltip = null;

    this.onResize = theme?.utils?.debounce
      ? theme.utils.debounce(() => this.updateActive(), 150)
      : () => this.updateActive();

    this.bindEvents();
  }

  bindEvents() {
    this.tooltips.forEach((tooltip) => {
      const icon = tooltip.closest('.content-item__tooltip-icon');
      if (!icon) return;

      icon.addEventListener('mouseenter', () => this.openTooltip(tooltip));
      icon.addEventListener('mouseleave', () => this.closeTooltip(tooltip));
    });

    window.addEventListener('resize', this.onResize);
  }

  openTooltip(tooltip) {
    this.activeTooltip = tooltip;
    tooltip.classList.add('is-open');
    tooltip.classList.remove('is-positioned');
    this.alignTooltip(tooltip);
  }

  closeTooltip(tooltip) {
    if (this.activeTooltip === tooltip) this.activeTooltip = null;
    this.resetTooltip(tooltip);
  }

  getInset() {
    const style = this.wrapper ? getComputedStyle(this.wrapper) : null;
    const left = style ? parseFloat(style.paddingLeft) : NaN;
    return Number.isFinite(left) && left > 0 ? left : 16;
  }

  alignTooltip(tooltip) {
    const panel = tooltip.querySelector('.metafield-rich_text_field');
    const icon = tooltip.closest('.content-item__tooltip-icon');
    if (!panel || !icon || !this.wrapper) return;

    this.resetPanel(panel);

    const inset = this.getInset();
    const wrapperRect = this.wrapper.getBoundingClientRect();
    const iconRect = icon.getBoundingClientRect();
    const maxWidth = Math.max(wrapperRect.width - inset * 2, 0);

    panel.style.setProperty('--tooltip-max-width', `${maxWidth}px`);

    const panelRect = panel.getBoundingClientRect();
    const iconCenterX = iconRect.left + iconRect.width / 2;
    const minLeft = wrapperRect.left + inset;
    const maxLeft = wrapperRect.right - inset - panelRect.width;
    const targetLeft = Math.max(minLeft, Math.min(maxLeft, iconCenterX - panelRect.width / 2));
    const shiftX = Math.round(targetLeft - panelRect.left);

    panel.style.setProperty('--tooltip-shift-x', `${shiftX}px`);

    if (targetLeft <= minLeft + 1) {
      panel.classList.add('metafield-rich_text_field--align-left');
    } else if (targetLeft >= maxLeft - 1) {
      panel.classList.add('metafield-rich_text_field--align-right');
    } else {
      panel.classList.add('metafield-rich_text_field--align-center');
    }

    tooltip.classList.add('is-positioned');
  }

  resetPanel(panel) {
    panel.classList.remove(...B2bSubscribeTooltips.ALIGN_CLASSES);
    panel.style.removeProperty('--tooltip-shift-x');
    panel.style.removeProperty('--tooltip-max-width');
  }

  resetTooltip(tooltip) {
    tooltip.classList.remove('is-open', 'is-positioned');
    const panel = tooltip.querySelector('.metafield-rich_text_field');
    if (panel) this.resetPanel(panel);
  }

  updateActive() {
    if (!this.activeTooltip) return;
    this.activeTooltip.classList.remove('is-positioned');
    this.alignTooltip(this.activeTooltip);
  }
}

function initB2bSubscribeTooltips(root = document) {
  root.querySelectorAll('.b2b-subscribe').forEach((section) => {
    if (section._b2bSubscribeTooltips) return;
    section._b2bSubscribeTooltips = new B2bSubscribeTooltips(section);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initB2bSubscribeTooltips());
} else {
  initB2bSubscribeTooltips();
}

document.addEventListener('shopify:section:load', (event) => {
  initB2bSubscribeTooltips(event.target);
});
